if (!customElements.get("pk-pdp-spatial-stage")) {
  customElements.define(
    "pk-pdp-spatial-stage",
    class PkPdpSpatialStage extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      stickyEvaluationFrame = undefined;
      containmentGuardFrame = undefined;
      layoutResizeObserver = undefined;
      stickyResumeScrollY = 0;

      connectedCallback() {
        this.sizeIndex = Number.parseInt(this.dataset.sizeIndex, 10);
        this.thicknessIndex = Number.parseInt(this.dataset.thicknessIndex, 10);
        this.gallery = this.closest(".product-media-gallery");
        this.header = document.querySelector("#lp-header, header.lp-header, sticky-header.shopify-section-header-sticky");
        this.room = this.querySelector("[data-pk-room]");
        this.roomImage = this.querySelector("[data-pk-room-image]");
        this.roomButton = this.querySelector("[data-pk-room-view]");
        this.arLink = this.querySelector("[data-pk-ar-entry]");

        this.setupLayoutObservers();

        const initialVariantNode = this.querySelector("[data-pk-initial-variant]");
        if (initialVariantNode) {
          try {
            const initialVariant = JSON.parse(initialVariantNode.textContent);
            this.update(initialVariant);
          } catch (error) {
            console.warn("PopsyKosy spatial stage initial variant could not be parsed.", error);
            this.failClosed();
          }
        } else {
          this.failClosed();
        }

        if (this.roomButton) {
          this.roomButton.addEventListener("click", this.handleRoomView);
        }

        if (typeof webvista !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
          this.variantChangeUnsubscriber = webvista.subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              if (String(event?.data?.sectionId ?? "") !== this.dataset.sectionId) return;
              this.update(event?.data?.variant);
            },
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.roomButton) this.roomButton.removeEventListener("click", this.handleRoomView);
        if (this.roomImage) this.roomImage.removeEventListener("load", this.scheduleStickyEvaluation);
        window.removeEventListener("resize", this.scheduleStickyEvaluation);
        window.removeEventListener("scroll", this.scheduleContainmentGuard);
        window.visualViewport?.removeEventListener("resize", this.scheduleStickyEvaluation);
        this.layoutResizeObserver?.disconnect();
        if (this.stickyEvaluationFrame !== undefined) cancelAnimationFrame(this.stickyEvaluationFrame);
        if (this.containmentGuardFrame !== undefined) cancelAnimationFrame(this.containmentGuardFrame);
        this.clearLayoutState();
      }

      setupLayoutObservers() {
        window.addEventListener("resize", this.scheduleStickyEvaluation, { passive: true });
        window.addEventListener("scroll", this.scheduleContainmentGuard, { passive: true });
        window.visualViewport?.addEventListener("resize", this.scheduleStickyEvaluation, { passive: true });
        this.roomImage?.addEventListener("load", this.scheduleStickyEvaluation);

        if (typeof ResizeObserver !== "undefined") {
          this.layoutResizeObserver = new ResizeObserver(this.scheduleStickyEvaluation);
          this.layoutResizeObserver.observe(this);
          if (this.header) this.layoutResizeObserver.observe(this.header);
        }

        if (document.fonts?.ready) {
          document.fonts.ready.then(this.scheduleStickyEvaluation).catch(() => {});
        }
      }

      scheduleStickyEvaluation = () => {
        if (this.stickyEvaluationFrame !== undefined) {
          cancelAnimationFrame(this.stickyEvaluationFrame);
        }
        this.stickyEvaluationFrame = requestAnimationFrame(() => {
          this.stickyEvaluationFrame = undefined;
          this.evaluateStickyFit();
        });
      };

      scheduleContainmentGuard = () => {
        if (this.containmentGuardFrame !== undefined) return;
        this.containmentGuardFrame = requestAnimationFrame(() => {
          this.containmentGuardFrame = undefined;
          this.guardStickyContainment();
        });
      };

      measureHeaderBottom() {
        if (!this.header) return 0;
        const style = getComputedStyle(this.header);
        if (style.display === "none" || style.visibility === "hidden") return 0;
        if (style.position !== "fixed" && style.position !== "sticky") return 0;
        const rect = this.header.getBoundingClientRect();
        return rect.bottom > 0 ? rect.bottom : 0;
      }

      evaluateStickyFit() {
        if (!this.gallery || this.hidden || !this.gallery.classList.contains("pk-spatial-stage-ready")) {
          this.clearStickyState();
          return;
        }

        if (!window.matchMedia("(min-width: 1400px)").matches) {
          this.clearStickyState("static-mobile");
          return;
        }

        this.gallery.classList.remove("pk-spatial-stage-sticky-fit", "pk-spatial-stage-sticky-probe");
        this.gallery.style.removeProperty("--pk-spatial-media-canvas-height");
        this.gallery.style.removeProperty("--pk-spatial-sticky-top");

        const flowRect = this.gallery.getBoundingClientRect();
        const flowTop = flowRect.top + window.scrollY;
        const mediaCanvas = this.gallery.querySelector(
          ".product-media-item.is-current-variant-media .product-media, .product-media-item:not([hidden]) .product-media, .product-media",
        );
        const squareStackHeight = flowRect.height;
        const squareCanvasRect = mediaCanvas?.getBoundingClientRect();
        const squareCanvasHeight = squareCanvasRect?.height || 0;
        const canvasWidth = squareCanvasRect?.width || 0;
        const ancillaryHeight = Math.max(0, squareStackHeight - squareCanvasHeight);
        const headerBottom = this.measureHeaderBottom();
        if (headerBottom > 0) {
          this.gallery.style.setProperty("--pk-spatial-sticky-top", `${Math.ceil(headerBottom + 16)}px`);
        }

        this.gallery.classList.add("pk-spatial-stage-sticky-probe");
        const stickyTop = Number.parseFloat(getComputedStyle(this.gallery).top);
        const viewportHeight = Math.min(
          window.innerHeight,
          window.visualViewport?.height || window.innerHeight,
        );
        const safetySpace = 16;
        const minimumCanvas = Math.min(520, canvasWidth);
        const maximumCanvas = viewportHeight - stickyTop - safetySpace - ancillaryHeight;
        const targetCanvas = Math.floor(Math.min(canvasWidth, 680, maximumCanvas));
        const fits =
          Number.isFinite(stickyTop) &&
          stickyTop >= headerBottom &&
          canvasWidth > 0 &&
          targetCanvas >= minimumCanvas;

        this.gallery.classList.remove("pk-spatial-stage-sticky-probe");
        this.stickyResumeScrollY = Math.max(0, flowTop - stickyTop);

        if (!fits) {
          this.clearStickyState("static-overflow", { preserveStickyTop: true });
          return;
        }

        this.gallery.style.setProperty("--pk-spatial-media-canvas-height", `${targetCanvas}px`);
        this.gallery.classList.add("pk-spatial-stage-sticky-fit");

        const renderedTop = this.gallery.getBoundingClientRect().top;
        if (renderedTop + 0.5 < stickyTop) {
          this.clearStickyState("static-containment", { preserveStickyTop: true });
          return;
        }

        this.gallery.dataset.pkStickyState = "fit-compact";
      }

      guardStickyContainment() {
        if (!this.gallery || this.hidden) return;

        if (this.gallery.classList.contains("pk-spatial-stage-sticky-fit")) {
          const stickyTop = Number.parseFloat(getComputedStyle(this.gallery).top);
          const renderedTop = this.gallery.getBoundingClientRect().top;
          if (!Number.isFinite(stickyTop) || renderedTop + 0.5 < stickyTop) {
            this.clearStickyState("static-containment", { preserveStickyTop: true });
          }
          return;
        }

        if (
          this.gallery.dataset.pkStickyState === "static-containment" &&
          window.scrollY <= this.stickyResumeScrollY + 1
        ) {
          this.scheduleStickyEvaluation();
        }
      }

      clearStickyState(state = "inactive", { preserveStickyTop = false } = {}) {
        if (!this.gallery) return;
        this.gallery.classList.remove("pk-spatial-stage-sticky-fit", "pk-spatial-stage-sticky-probe");
        this.gallery.style.removeProperty("--pk-spatial-media-canvas-height");
        if (!preserveStickyTop) this.gallery.style.removeProperty("--pk-spatial-sticky-top");
        this.gallery.dataset.pkStickyState = state;
      }

      clearLayoutState() {
        this.gallery?.classList.remove(
          "pk-spatial-stage-ready",
          "pk-spatial-stage-sticky-fit",
          "pk-spatial-stage-sticky-probe",
        );
        this.gallery?.style.removeProperty("--pk-spatial-media-canvas-height");
        this.gallery?.style.removeProperty("--pk-spatial-sticky-top");
        if (this.gallery) delete this.gallery.dataset.pkStickyState;
      }

      handleRoomView = () => {
        const mediaId = this.roomButton?.dataset.mediaId;
        if (!mediaId) return;

        const thumbnail = this.gallery?.querySelector(`[data-target="${mediaId}"] .thumbnail-media`);
        if (thumbnail) {
          thumbnail.click();
          thumbnail.focus({ preventScroll: true });
        }
      };

      update(variant) {
        if (!variant || !Array.isArray(variant.options) || variant.available !== true) {
          this.failClosed();
          return;
        }
        if (
          !Number.isInteger(this.sizeIndex) ||
          !Number.isInteger(this.thicknessIndex) ||
          this.sizeIndex < 0 ||
          this.thicknessIndex < 0 ||
          this.sizeIndex === this.thicknessIndex ||
          this.sizeIndex >= variant.options.length ||
          this.thicknessIndex >= variant.options.length
        ) {
          this.failClosed();
          return;
        }

        const thickness = variant.options[this.thicknessIndex];
        if (thickness !== '0.5"' && thickness !== '1"') {
          this.failClosed();
          return;
        }

        const size = this.normalizeSize(variant.options[this.sizeIndex]);
        const coverage = this.coverageFor(size.key);
        if (!coverage) {
          this.failClosed();
          return;
        }

        this.hidden = false;
        this.gallery?.classList.add("pk-spatial-stage-ready");
        this.setText("[data-pk-thickness]", thickness);
        this.setText("[data-pk-size]", size.label);
        this.setTextAll("[data-pk-tiles]", coverage.tiles);
        this.setTextAll("[data-pk-area]", coverage.area);
        this.updateRoom(variant.id);
        this.scheduleStickyEvaluation();
      }

      normalizeSize(value = "") {
        const match = String(value).match(/^\s*(4\s*[x×]\s*6|6\s*[x×]\s*8|8\s*[x×]\s*12|10\s*[x×]\s*12)\s*$/i);
        if (!match) return { key: "", label: String(value) };
        const dimensions = match[1].split(/\s*[x×]\s*/i);
        return {
          key: `${dimensions[0]}x${dimensions[1]}`,
          label: `${dimensions[0]} × ${dimensions[1]}`,
        };
      }

      updateRoom(variantId) {
        const mapping = this.querySelector(`[data-pk-variant-spatial][data-variant-id="${variantId}"]`);
        const roomMediaId = mapping?.dataset.roomMediaId || "";
        const roomSrc = mapping?.dataset.roomSrc || "";
        const roomAlt = mapping?.dataset.roomAlt || "";
        const arUsdz = mapping?.dataset.arUsdz || "";
        const arGlb = mapping?.dataset.arGlb || "";

        if (!this.room || !this.roomImage || (!roomSrc && !arUsdz)) {
          this.hideRoom();
          return;
        }

        this.room.hidden = false;
        if (roomSrc) {
          this.roomImage.src = roomSrc;
          this.roomImage.alt = roomAlt;
          this.roomImage.hidden = false;
        } else {
          this.roomImage.removeAttribute("src");
          this.roomImage.alt = "";
          this.roomImage.hidden = true;
        }

        if (arUsdz && this.arLink) {
          this.arLink.href = arUsdz;
          this.arLink.dataset.arGlb = arGlb;
          this.arLink.hidden = false;
          if (this.roomButton) this.roomButton.hidden = true;
          this.scheduleStickyEvaluation();
          return;
        }

        if (roomMediaId && roomSrc && this.roomButton) {
          this.roomButton.dataset.mediaId = roomMediaId;
          this.roomButton.hidden = false;
          if (this.arLink) this.arLink.hidden = true;
          this.scheduleStickyEvaluation();
          return;
        }

        this.hideRoom();
      }

      hideRoom() {
        if (this.room) this.room.hidden = true;
        if (this.roomImage) {
          this.roomImage.removeAttribute("src");
          this.roomImage.alt = "";
          this.roomImage.hidden = true;
        }
        if (this.roomButton) {
          this.roomButton.hidden = true;
          delete this.roomButton.dataset.mediaId;
        }
        if (this.arLink) {
          this.arLink.hidden = true;
          this.arLink.removeAttribute("href");
          delete this.arLink.dataset.arGlb;
        }
        this.scheduleStickyEvaluation();
      }

      failClosed() {
        this.hidden = true;
        this.clearLayoutState();
        this.hideRoom();
        this.setText("[data-pk-thickness]", "");
        this.setText("[data-pk-size]", "");
        this.setTextAll("[data-pk-tiles]", "");
        this.setTextAll("[data-pk-area]", "");
      }

      coverageFor(key) {
        return {
          "4x6": { tiles: 6, area: 24 },
          "6x8": { tiles: 12, area: 48 },
          "8x12": { tiles: 24, area: 96 },
          "10x12": { tiles: 30, area: 120 },
        }[key];
      }

      setText(selector, value) {
        const node = this.querySelector(selector);
        if (node) node.textContent = value ?? "";
      }

      setTextAll(selector, value) {
        this.querySelectorAll(selector).forEach((node) => {
          node.textContent = value ?? "";
        });
      }
    },
  );
}
